import styled, { css } from 'styled-components';

const buildTooltipStyles = (selectors) => {
  const list = Array.isArray(selectors) ? selectors : [selectors];
  const baseSelectors = list.join(',\n  ');
  const afterSelectors = list.map((selector) => `${selector}::after`).join(',\n  ');
  const beforeSelectors = list.map((selector) => `${selector}::before`).join(',\n  ');
  const afterVisibleSelectors = list.map((selector) => `${selector}:hover::after, ${selector}:focus-visible::after`).join(',\n  ');
  const beforeVisibleSelectors = list.map((selector) => `${selector}:hover::before, ${selector}:focus-visible::before`).join(',\n  ');

  return css`
  ${baseSelectors} {
    position: relative;
  }

  ${afterSelectors} {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 10px);
    transform: translate(-50%, 6px);
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(20, 21, 23, 0.96);
    color: #f8fafc;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.01em;
    pointer-events: none;
    opacity: 0;
    z-index: 60;
    transition: opacity 120ms ease, transform 120ms ease;
  }

  ${beforeSelectors} {
    content: '';
    position: absolute;
    left: 50%;
    bottom: calc(100% + 4px);
    transform: translateX(-50%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid rgba(20, 21, 23, 0.96);
    pointer-events: none;
    opacity: 0;
    z-index: 60;
    transition: opacity 120ms ease;
  }

  ${afterVisibleSelectors} {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  ${beforeVisibleSelectors} {
    opacity: 1;
  }
`;
};

const meetingTooltipSelectors = [
  '.meeting-action-btn[data-tooltip]',
  '.dock-icon-btn[data-tooltip]',
  '.stage-layout-toggle-wrap[data-tooltip]',
  '.pip-mode-more[data-tooltip]',
];

const waitingRoomTooltipSelector = '.waiting-room-tooltip[data-tooltip]';

export const StyledLayout = styled.main`
  height: calc(100vh - 70px);
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #0f1720;
  overflow: hidden;
`;

export const StyledTopBar = styled.div`
  height: 64px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  gap: 0.75rem;
  border-bottom: 1px solid #dbe6ef;
  background: #ffffff;

  .logo {
    font-size: 1rem;
    font-weight: 700;
    background: linear-gradient(135deg, #2eaac7, #5bc5e0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .title {
    font-size: 0.95rem;
    font-weight: 500;
    color: #102437;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sep {
    width: 1px;
    height: 20px;
    background: #e4e7eb;
    flex-shrink: 0;
  }

  .spacer {
    margin-left: auto;
  }

  .time {
    font-size: 0.8125rem;
    color: #516679;
    font-variant-numeric: tabular-nums;
  }

  .icon-btn {
    background: transparent;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    transition: background 0.15s, color 0.15s;
    position: relative;
  }

  .icon-btn:hover {
    background: #f0f2f4;
    color: #1a1a1a;
  }

  .icon-btn.active {
    color: #2eaac7;
    background: #e6f5f8;
  }

  .badge {
    background: #2eaac7;
    color: #fff;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 700;
    padding: 1px 5px;
    min-width: 16px;
    text-align: center;
    position: absolute;
    top: 2px;
    right: 2px;
  }
`;

export const StyledContent = styled.div`
  position: relative;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.625rem;
  box-sizing: border-box;

  .stage-main {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: row;
    gap: 0.625rem;
  }

  .videos {
    flex: 1;
    min-height: 0;
    min-width: 0;
    background: transparent;
    position: relative;
    padding: 0.25rem;
    border-radius: 16px;
    border: none;
    box-sizing: border-box;
  }

  .videos [data-testid='video-grid'] {
    background: transparent !important;
    gap: 10px;
  }

  .dock-icon-btn {
    background: #3c4043;
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #ffffff;
    border-radius: 999px;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    transition: background 140ms ease, border-color 140ms ease;
  }

  ${buildTooltipStyles(meetingTooltipSelectors)}

  .dock-icon-btn:hover {
    background: #4a4d51;
    border-color: rgba(255, 255, 255, 0.12);
  }

  .dock-icon-btn.active {
    background: rgba(46, 170, 199, 0.24);
    border-color: rgba(46, 170, 199, 0.9);
    color: #b8f0ff;
  }

  .dock-icon-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #2d3033;
    border-color: rgba(255, 255, 255, 0.06);
    color: #9aa0a6;
  }

  .dock-icon-btn:disabled:hover {
    background: #2d3033;
    border-color: rgba(255, 255, 255, 0.06);
  }

  .stage-layout-toggle-wrap[data-tooltip]::after {
    left: 0;
    top: calc(100% + 10px);
    bottom: auto;
    max-width: none;
    transform: translateY(-6px);
  }

  .stage-layout-toggle-wrap[data-tooltip]::before {
    top: calc(100% + 4px);
    bottom: auto;
    border-top: 0;
    border-bottom: 6px solid rgba(20, 21, 23, 0.96);
  }

  .stage-layout-toggle-wrap[data-tooltip]:hover::after,
  .stage-layout-toggle-wrap[data-tooltip]:focus-visible::after {
    transform: translateY(0);
  }

  .dock-icon-btn .badge {
    background: #2eaac7;
    color: #fff;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 700;
    padding: 1px 5px;
    min-width: 16px;
    text-align: center;
    position: absolute;
    top: -4px;
    right: -3px;
  }

  .controls {
    position: relative;
    z-index: 10;
    display: flex;
    justify-content: center;
    pointer-events: all;
    margin-bottom: 2px;
  }

  .controls > * {
    pointer-events: all;
  }

  .panel {
    width: 336px;
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 14px;
    background: #0f1720;
    color: #ffffff;
    overflow: hidden;
  }

  @media screen and (max-width: 768px) {
    height: 100%;
    padding: 0.5rem;
    gap: 0.4rem;

    .stage-main {
      gap: 0.5rem;
    }

    .controls {
      position: relative;
      margin-top: 0.3rem;
    }

    .panel {
      display: none;
    }
  }
`;

export const StyledControls = styled.div`
  opacity: 1;
  transition: none;

  .meeting-dock {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 58px;
    width: min(1120px, calc(100vw - 36px));
    border-radius: 16px;
    padding: 8px 10px;
    background: rgba(32, 33, 36, 0.94);
    border: 1px solid rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(14px);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.34);
  }

  .dock-left {
    width: 210px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 8px;
  }

  .meeting-title {
    color: #f1f5f9;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meeting-time {
    color: #b8c3cf;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }

  .dock-center {
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }

  .dock-right {
    width: 112px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-right: 6px;
  }

  .meeting-action-btn {
    height: 44px;
    width: 44px;
    min-width: 44px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: #3c4043;
    color: #ffffff;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    font-size: 11px;
    letter-spacing: 0.01em;
    font-weight: 600;
    cursor: pointer;
    transition: background 140ms ease, border-color 140ms ease;
  }

  .meeting-action-btn.mic-tester-btn {
    width: 88px;
    min-width: 88px;
  }

  .meeting-action-btn:hover {
    background: #4a4d51;
    border-color: rgba(255, 255, 255, 0.12);
  }

  .meeting-action-btn.off {
    color: #ffffff;
    background: #ea4335;
    border-color: #ea4335;
  }

  .meeting-action-btn.active {
    color: #b8f0ff;
    background: rgba(46, 170, 199, 0.3);
    border-color: rgba(46, 170, 199, 0.9);
  }

  .meeting-action-btn.leave {
    color: #fff;
    background: #ea4335;
    border-color: #ea4335;
  }

  .meeting-action-btn.leave:hover {
    background: #d93b2f;
    border-color: #d93b2f;
  }

  .meeting-action-btn:disabled {
    opacity: 0.7;
    cursor: default;
  }

  .pip-menu-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .pip-menu-wrap:has(> .pip-mode-more) {
    align-items: stretch;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    background: #3c4043;
    overflow: visible;
    transition: border-color 140ms ease, box-shadow 140ms ease;
  }

  .pip-menu-wrap:has(> .pip-mode-more):has(> .meeting-action-btn.active) {
    border-color: rgba(46, 170, 199, 0.9);
    box-shadow: 0 0 0 1px rgba(46, 170, 199, 0.18);
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn {
    border: none;
    background: transparent;
    border-top-left-radius: 999px;
    border-bottom-left-radius: 999px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    width: 44px;
    height: 44px;
    min-width: 44px;
    transition: none;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn.mic-tester-btn {
    width: 88px;
    min-width: 88px;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn:hover {
    background: #4a4d51;
    border: none;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn.off {
    background: #ea4335;
    color: #ffffff;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn.off:hover {
    background: #d93b2f;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn.active {
    background: rgba(46, 170, 199, 0.3);
    color: #b8f0ff;
    border: none;
  }

  .pip-menu-wrap:has(> .pip-mode-more) > .meeting-action-btn.active:hover {
    background: rgba(46, 170, 199, 0.38);
  }

  .pip-mode-more {
    width: 22px;
    height: 44px;
    border: none;
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    background: transparent;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background 140ms ease;
  }

  .pip-mode-more:hover {
    background: #4a4d51;
  }

  .pip-mode-menu {
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    background: rgba(32, 33, 36, 0.98);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 14px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: max-content;
    width: max-content;
    max-width: min(320px, calc(100vw - 24px));
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.34);
    z-index: 30;
  }

  .pip-mode-menu button {
    border: none;
    background: transparent;
    color: #f1f3f4;
    text-align: left;
    font-size: 12px;
    font-weight: 500;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .pip-mode-menu button:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .pip-mode-menu button.active {
    background: rgba(46, 170, 199, 0.22);
    color: #b9f2ff;
  }

  @media screen and (max-width: 768px) {
    opacity: 1;

    .meeting-dock {
      width: calc(100vw - 20px);
      min-height: 0;
      border-radius: 14px;
      padding: 8px;
      flex-wrap: wrap;
      justify-content: center;
      gap: 8px;
    }

    .dock-left,
    .dock-right {
      width: auto;
      padding: 0;
    }

    .dock-left {
      order: 2;
      align-items: center;
      text-align: center;
    }

    .dock-center {
      order: 1;
      width: 100%;
      flex-wrap: wrap;
      gap: 6px;
    }

    .dock-right {
      order: 3;
    }
  }

`;

export const StyledDeviceSetupLayout = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  max-width: 85rem;
  padding: 2rem;
  margin: auto;

  ${buildTooltipStyles(waitingRoomTooltipSelector)}

  @media (max-width: 760px) {
    align-items: unset;
    justify-content: unset;
    padding: 1rem;
  }
`;
